import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import {
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import {
  Loader2,
  AlertCircle,
} from "lucide-react";

import PetGridSection from "../../components/customer/PetGridSection";

import {
  useLazyGetPetFiltersQuery,
  usePetListingOrSearchMutation,
} from "../../redux/actions/Customer/serviceCustomer";

import { IPetSearchRequest } from "../../types/Request/CustomerRequest";
import { IPetItem } from "../../types/Response/CustomerResponse";

import PetFilterSidebar from "../../components/customer/PetFilterSidebar";

const CustomerPetListingScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // ============================================================
  // ROUTE DATA
  // ============================================================

  const source = location.state?.source;
  const stateData = location.state?.data;

  const title =
    stateData?.title ||
    location.state?.title;

  const bannerImage =
    stateData?.mainImage ||
    stateData?.imageUrl ||
    location.state?.bannerImage;

  const routeCategoryId =
    searchParams.get("categoryId") ||
    searchParams.get("categoryIds") ||
    location.state?.categoryId ||
    location.state?.categoryIds ||
    stateData?.categoryId;

  // ============================================================
  // API
  // ============================================================

  const [
    getPetFilters,
    {
      data: filterResponse,
      isLoading: isLoadingFilters,
    },
  ] = useLazyGetPetFiltersQuery();

  const [
    searchPets,
    {
      data: petSearchResult,
      isLoading: isSearchingPets,
    },
  ] = usePetListingOrSearchMutation();

  // ============================================================
  // DOG / CAT UI FILTER
  //
  // null = show all
  // Dog = show dogs
  // Cat = show cats
  // ============================================================

  const [selectedPetType, setSelectedPetType] = useState<
    "Dog" | "Cat" | null
  >(null);

  // ============================================================
  // SEARCH REQUEST
  //
  // IMPORTANT:
  // We do NOT put Dog/Cat into petType.
  //
  // API petType means Adopt/Sell.
  // ============================================================

  const [
    searchRequest,
    setSearchRequest,
  ] = useState<IPetSearchRequest>({
    query:
      searchParams.get("query") ||
      undefined,

    pageNumber: 1,

    pageSize: 20,

    sortBy: "Relevance",

    categoryIds: routeCategoryId
      ? [Number(routeCategoryId)]
      : [],

    breeds: [],

    genders: [],

    petType: undefined,

    petStatus: undefined,

    minPrice: undefined,

    maxPrice: undefined,

    isFeatured: undefined,

    tag: undefined,
  });

  // ============================================================
  // LOAD FILTERS
  // ============================================================

  useEffect(() => {
    getPetFilters();
  }, [getPetFilters]);

  // ============================================================
  // CATEGORY ID
  // ============================================================

  useEffect(() => {
    if (routeCategoryId) {
      setSearchRequest((prev) => ({
        ...prev,
        categoryIds: [Number(routeCategoryId)],
        pageNumber: 1,
      }));
    }
  }, [routeCategoryId]);

  // ============================================================
  // FETCH PETS
  // ============================================================

  const fetchPets = useCallback(async () => {
    try {
      await searchPets(searchRequest).unwrap();
    } catch (error) {
      console.error(
        "Failed to load pet listings:",
        error
      );
    }
  }, [
    searchPets,
    searchRequest,
  ]);

  useEffect(() => {
    fetchPets();
  }, [fetchPets]);

  // ============================================================
  // DOG / CAT UI FILTER
  //
  // IMPORTANT:
  // This does NOT call the API.
  //
  // Clicking selected button again => null => all pets.
  // ============================================================

  const handlePetTypeChange = (
    type: "Dog" | "Cat"
  ) => {
    setSelectedPetType((current) =>
      current === type
        ? null
        : type
    );
  };

  // ============================================================
  // ALL PETS FROM API
  // ============================================================

  const petsList: IPetItem[] =
    petSearchResult?.items || [];

  // ============================================================
  // FILTER PETS ON UI
  //
  // Your API data looks like:
  //
  // categories: [
  //   {
  //      name: "Dog",
  //      slug: "pets-dog"
  //   }
  // ]
  //
  // So we check categories[].name
  // ============================================================

  const filteredPets = useMemo(() => {
    // No Dog/Cat selected
    // => show everything
    if (!selectedPetType) {
      return petsList;
    }

    return petsList.filter((pet: any) => {
      const categories =
        Array.isArray(pet.categories)
          ? pet.categories
          : [];

      return categories.some(
        (category: any) => {
          const categoryName =
            String(
              category?.name || ""
            ).trim().toLowerCase();

          const categorySlug =
            String(
              category?.slug || ""
            ).trim().toLowerCase();

          if (
            selectedPetType === "Dog"
          ) {
            return (
              categoryName === "dog" ||
              categorySlug === "pets-dog"
            );
          }

          if (
            selectedPetType === "Cat"
          ) {
            return (
              categoryName === "cat" ||
              categorySlug === "pets-cat"
            );
          }

          return false;
        }
      );
    });
  }, [
    petsList,
    selectedPetType,
  ]);

  // ============================================================
  // TOTAL COUNT
  // ============================================================

  const totalPets =
    selectedPetType
      ? filteredPets.length
      : petSearchResult?.totalCount ??
        petsList.length;

  // ============================================================
  // PET DETAIL
  // ============================================================

  const handlePetPage = (
    pet: IPetItem | any
  ) => {
    const id =
      pet.petId ||
      pet.id ||
      pet.PetId;

    navigate(
      `/productpage/PetDetailScreen/${id}`
    );
  };

  // ============================================================
  // SIDEBAR FILTER
  // ============================================================

  const handleFilterChange = (
    updatedReq: IPetSearchRequest
  ) => {
    setSearchRequest((prev) => ({
      ...prev,
      ...updatedReq,
      pageNumber: 1,
    }));
  };

  // ============================================================
  // CLEAR FILTERS
  // ============================================================

  const handleClearFilters = () => {
    setSelectedPetType(null);

    setSearchRequest({
      query:
        searchParams.get("query") ||
        undefined,

      pageNumber: 1,

      pageSize: 20,

      sortBy: "Relevance",

      categoryIds: [],

      breeds: [],

      genders: [],

      petType: undefined,

      petStatus: undefined,

      minPrice: undefined,

      maxPrice: undefined,

      isFeatured: undefined,

      tag: undefined,
    });
  };

  // ============================================================
  // UI
  // ============================================================

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">

      <div className="flex flex-col lg:flex-row gap-8 items-start">

        {/* ======================================================
            LEFT SIDEBAR
        ====================================================== */}

        <aside className="hidden lg:block lg:w-[260px] flex-shrink-0 top-6 self-start">

          {filterResponse ? (
            <PetFilterSidebar
              filters={filterResponse}
              searchRequest={searchRequest}
              onChange={handleFilterChange}
            />
          ) : isLoadingFilters ? (
            <div className="p-4 bg-gray-50 rounded-2xl flex items-center gap-2 text-xs text-gray-400">

              <Loader2
                className="
                  w-4
                  h-4
                  animate-spin
                  text-[#2F5E2E]
                "
              />

              Loading filters...

            </div>
          ) : null}

        </aside>

        {/* ======================================================
            MAIN
        ====================================================== */}

        <main className="flex-1 w-full">

          {/* ====================================================
              BANNER
          ==================================================== */}

          {(source === "BrandsSlider" ||
            source === "PeekCarousel" ||
            bannerImage) &&
            bannerImage && (

              <div className="w-full mb-6 rounded-2xl overflow-hidden shadow-sm border border-gray-100">

                <img
                  src={bannerImage}
                  alt={
                    title ||
                    "Pet Listing Banner"
                  }
                  className="
                    w-full
                    h-44
                    md:h-52
                    object-cover
                  "
                />

              </div>
            )}

          {/* ====================================================
              HEADER
          ==================================================== */}

          <div className="mb-6 pb-4 border-b border-gray-100">

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">

              {/* TITLE */}

              <div>

                <h1 className="text-2xl font-bold text-logoHeading tracking-tight">
                  {title || "Explore Pets"}
                </h1>

                <p className="text-xs text-gray-400 mt-0.5">
                  Showing{" "}
                  {filteredPets.length}{" "}
                  of{" "}
                  {totalPets}{" "}
                  companions
                </p>

              </div>

              {/* ==================================================
                  DOG / CAT BUTTONS
              ================================================== */}

              <div className="flex items-center gap-2">

                {/* DOG */}

                <button
                  type="button"
                  onClick={() =>
                    handlePetTypeChange("Dog")
                  }
                  className={`
                    px-5
                    py-2.5
                    rounded-xl
                    border
                    text-sm
                    font-semibold
                    transition-all
                    duration-200
                    cursor-pointer
                    ${
                      selectedPetType === "Dog"
                        ? `
                          bg-[#2F5E2E]
                          border-[#2F5E2E]
                          text-white
                          shadow-sm
                        `
                        : `
                          bg-white
                          border-gray-200
                          text-gray-600
                          hover:border-[#2F5E2E]
                          hover:text-[#2F5E2E]
                        `
                    }
                  `}
                >
                  🐶 Dogs
                </button>

                {/* CAT */}

                <button
                  type="button"
                  onClick={() =>
                    handlePetTypeChange("Cat")
                  }
                  className={`
                    px-5
                    py-2.5
                    rounded-xl
                    border
                    text-sm
                    font-semibold
                    transition-all
                    duration-200
                    cursor-pointer
                    ${
                      selectedPetType === "Cat"
                        ? `
                          bg-[#2F5E2E]
                          border-[#2F5E2E]
                          text-white
                          shadow-sm
                        `
                        : `
                          bg-white
                          border-gray-200
                          text-gray-600
                          hover:border-[#2F5E2E]
                          hover:text-[#2F5E2E]
                        `
                    }
                  `}
                >
                  🐱 Cats
                </button>

              </div>

            </div>

          </div>

          {/* ====================================================
              LOADING
          ==================================================== */}

          {isSearchingPets ? (

            <div className="py-20 flex flex-col items-center justify-center text-slate-400 space-y-3">

              <Loader2
                className="
                  w-8
                  h-8
                  animate-spin
                  text-[#2F5E2E]
                "
              />

              <p className="text-xs font-bold uppercase tracking-wider">
                Finding available pets...
              </p>

            </div>

          ) : filteredPets.length > 0 ? (

            /* ==================================================
                PET GRID
            ================================================== */

            <PetGridSection
              pets={filteredPets}
              handlePetPage={handlePetPage}
            />

          ) : (

            /* ==================================================
                NO PETS
            ================================================== */

            <div className="
              py-16
              px-4
              text-center
              bg-gray-50
              border
              border-dashed
              border-gray-200
              rounded-3xl
              space-y-3
            ">

              <AlertCircle
                className="
                  w-10
                  h-10
                  text-gray-400
                  mx-auto
                "
              />

              <h3 className="
                text-base
                font-bold
                text-slate-700
              ">
                No Pets Found
              </h3>

              <p className="
                text-xs
                text-gray-500
                max-w-sm
                mx-auto
              ">
                No pets found for the
                selected type.
              </p>

              <button
                type="button"
                onClick={() =>
                  handleClearFilters()
                }
                className="
                  bg-[#2F5E2E]
                  text-white
                  text-xs
                  font-bold
                  px-4
                  py-2
                  rounded-xl
                  hover:bg-[#244923]
                  transition
                  cursor-pointer
                "
              >
                Show All Pets
              </button>

            </div>
          )}

        </main>

      </div>

    </div>
  );
};

export default CustomerPetListingScreen;